import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AEPNDownloadParams,
  ExportContributionsParams,
  GetAEPNListParams,
  GetAEPNListResponse,
  GetContributionsErrorsParams,
  GetContributionsHistoryParams,
  GetPayrollPendingParams,
  GetUploadHistoryParams,
  ResubmitContributionsParams,
  SubmitContributionsParams,
} from '../types';

// --- AEPN List ---
export const getAEPNList = createAsyncThunk<
  GetAEPNListResponse,
  GetAEPNListParams
>(
  'aepn/getAEPNList',
  async (params: GetAEPNListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.aepn.main, { params });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// --- AEPN Upload ---
export const aepnUpload = createAsyncThunk(
  'aepn/upload',
  async (data: { url: string; fileName: string; fileSize: number }, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.aepn.upload, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const aepnUploadSave = createAsyncThunk(
  'aepn/uploadSave',
  async (data: { aepnUploadIds: number[] }, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.aepn.uploadSave, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getUploadHistory = createAsyncThunk(
  'aepn/getUploadHistory',
  async (params: GetUploadHistoryParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.aepn.uploadHistory, { params });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getUploadHistoryById = createAsyncThunk(
  'aepn/getUploadHistoryById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.aepn.uploadHistory}/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// --- AEPN Download ---
export const aepnDownload = createAsyncThunk(
  'aepn/download',
  async (params: AEPNDownloadParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.aepn.download, { params });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const aepnDownloadForEmployee = createAsyncThunk(
  'aepn/downloadForEmployee',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.aepn.downloadForEmployee}/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const aepnDownloadForCompany = createAsyncThunk(
  'aepn/downloadForCompany',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.aepn.downloadForCompany}/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// --- Payroll Pending ---
export const getPayrollPending = createAsyncThunk(
  'aepn/getPayrollPending',
  async (params: GetPayrollPendingParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.aepn.payrollPending, { params });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// --- Contributions ---
export const getContributionsHistory = createAsyncThunk(
  'aepn/getContributionsHistory',
  async (params: GetContributionsHistoryParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.aepn.contributionsHistory, { params });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const submitContributions = createAsyncThunk(
  'aepn/submitContributions',
  async (params: SubmitContributionsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.aepn.contributionsSubmit, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resubmitContributions = createAsyncThunk(
  'aepn/resubmitContributions',
  async (params: ResubmitContributionsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.aepn.contributionsResubmit, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getContributionsStatus = createAsyncThunk(
  'aepn/getContributionsStatus',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.aepn.contributionsStatus);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getContributionsErrors = createAsyncThunk(
  'aepn/getContributionsErrors',
  async ({ submissionId, ...params }: GetContributionsErrorsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.aepn.contributionsErrors}/${submissionId}`,
        { params },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const exportContributions = createAsyncThunk(
  'aepn/exportContributions',
  async ({ companyId, ...params }: ExportContributionsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.aepn.contributionsExport}/${companyId}`,
        params,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const aepnActs = {
  getAEPNList,
  // Upload
  aepnUpload,
  aepnUploadSave,
  getUploadHistory,
  getUploadHistoryById,
  // Download
  aepnDownload,
  aepnDownloadForEmployee,
  aepnDownloadForCompany,
  // Payroll pending
  getPayrollPending,
  // Contributions
  getContributionsHistory,
  submitContributions,
  resubmitContributions,
  getContributionsStatus,
  getContributionsErrors,
  exportContributions,
};
